import React, { useEffect, useState, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { grafikService } from '../../../services/grafikService';
import {
  formatMonthYear, getMonthsList, pl
} from '../../../utils/grafikHelpers';

/**
 * Raport punktualnosci per pracownik z eksportem CSV.
 *
 * Props:
 *   year, month, locationId
 *   onYearChange, onMonthChange, onLocationChange
 *   locations: array
 *   users: array  (z {login} co najmniej)
 */
const punctColor = (percent) => {
  const p = Number(percent || 0);
  if (p >= 95) return 'var(--success)';
  if (p >= 80) return 'var(--warning)';
  return 'var(--danger)';
};

const PunktualnoscRaport = ({
  year, month, locationId,
  onYearChange, onMonthChange, onLocationChange,
  locations = [],
  users = []
}) => {
  const today = new Date();
  const [filterUser, setFilterUser] = useState('');
  const [users_, setUsers_] = useState([]);
  const [details, setDetails] = useState({}); // {userLogin: shifts[]}
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await grafikService.getPunktualnoscRaport({
        year, month,
        locationId: locationId || undefined,
        userLogin: filterUser || undefined
      });
      const payload = res?.data || res || {};
      const arr = Array.isArray(payload.users) ? payload.users : [];
      setUsers_(arr);
    } catch (err) {
      toast.error(err.message || 'Blad pobierania raportu punktualnosci');
      setUsers_([]);
    } finally {
      setLoading(false);
    }
  }, [year, month, locationId, filterUser]);

  useEffect(() => { load(); }, [load]);

  const yearsRange = useMemo(() => {
    const cur = today.getFullYear();
    return [cur - 1, cur, cur + 1];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExportCsv = async () => {
    try {
      const blob = await grafikService.exportPunktualnoscCsv({
        year, month,
        locationId: locationId || undefined,
        userLogin: filterUser || undefined
      });
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      const userSuffix = filterUser ? `_${filterUser}` : '';
      const locSuffix = locationId ? `_loc${locationId}` : '_all';
      a.download = `punktualnosc_${year}_${String(month).padStart(2, '0')}${locSuffix}${userSuffix}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV pobrany');
    } catch (err) {
      toast.error(err.message || 'Blad eksportu CSV');
    }
  };

  const goPrevMonth = () => {
    if (month === 1) {
      onMonthChange && onMonthChange(12);
      onYearChange && onYearChange(year - 1);
    } else {
      onMonthChange && onMonthChange(month - 1);
    }
  };
  const goNextMonth = () => {
    if (month === 12) {
      onMonthChange && onMonthChange(1);
      onYearChange && onYearChange(year + 1);
    } else {
      onMonthChange && onMonthChange(month + 1);
    }
  };

  const toggleExpand = async (userLogin) => {
    if (expanded[userLogin]) {
      setExpanded(prev => ({ ...prev, [userLogin]: false }));
      return;
    }
    // Lazy-load detali (plan-vs-real dla tego usera w tym miesiacu)
    if (!details[userLogin]) {
      try {
        const res = await grafikService.getPlanVsReal({
          year, month,
          locationId: locationId || undefined
        });
        const payload = res?.data || res || {};
        const allShifts = Array.isArray(payload.shifts) ? payload.shifts : [];
        const userShifts = allShifts.filter(s => s.user_login === userLogin);
        setDetails(prev => ({ ...prev, [userLogin]: userShifts }));
      } catch (err) {
        toast.error(err.message || 'Blad pobierania detali');
        return;
      }
    }
    setExpanded(prev => ({ ...prev, [userLogin]: true }));
  };

  const btnSecondary = {
    padding: '6px 12px',
    backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)',
    border: '1px solid var(--border)', cursor: 'pointer',
    fontSize: '12px', fontWeight: 500
  };
  const inputStyle = {
    padding: '6px 10px',
    backgroundColor: 'var(--card-bg)', color: 'var(--text)',
    border: '1px solid var(--border)', borderRadius: '6px', fontSize: '12px'
  };

  return (
    <div>
      {/* Filtry */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem',
        padding: '0.75rem 1rem', backgroundColor: 'var(--bg)',
        border: '1px solid var(--border)', borderRadius: '6px',
        marginBottom: '0.75rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <button onClick={goPrevMonth} style={btnSecondary}>&laquo;</button>
          <select value={month} onChange={(e) => onMonthChange && onMonthChange(Number(e.target.value))} style={inputStyle}>
            {getMonthsList().map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <select value={year} onChange={(e) => onYearChange && onYearChange(Number(e.target.value))} style={inputStyle}>
            {yearsRange.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={goNextMonth} style={btnSecondary}>&raquo;</button>
        </div>

        <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text)', marginLeft: '0.5rem' }}>
          {formatMonthYear(year, month)}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginLeft: '0.5rem' }}>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Lokalizacja:</span>
          <select
            value={locationId || ''}
            onChange={(e) => onLocationChange && onLocationChange(e.target.value)}
            style={inputStyle}
          >
            <option value="">— wszystkie —</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.nazwa || l.name}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Pracownik:</span>
          <select
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
            style={inputStyle}
          >
            <option value="">— wszyscy —</option>
            {users.map(u => <option key={u.login} value={u.login}>{u.login}</option>)}
          </select>
        </div>

        <div style={{ flex: 1 }} />

        <button onClick={handleExportCsv} style={btnSecondary}>
          <i className="fas fa-file-csv" style={{ marginRight: '0.35rem' }} />
          Eksport CSV
        </button>
      </div>

      {/* Tabela */}
      <div style={{
        border: '1px solid var(--border)', borderRadius: '6px',
        backgroundColor: 'var(--card-bg)', overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            <div className="spinner-border spinner-border-sm" style={{ marginRight: '0.4rem' }} />
            Ladowanie raportu...
          </div>
        ) : users_.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            Brak danych do wyswietlenia.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                  {['', 'Pracownik', 'Zmian', '% Punktualnosci', 'Punktualnie', 'Spoznienia', 'Nieobecnosci', 'Suma min spozn.', 'Sr. min spozn.', 'Plan h', 'Real h'].map((h, i) => (
                    <th key={i} style={{
                      padding: '0.5rem 0.6rem', textAlign: i <= 1 ? 'left' : 'right',
                      fontSize: '0.7rem', fontWeight: 700,
                      color: 'var(--text-muted)', textTransform: 'uppercase',
                      letterSpacing: '0.03em', whiteSpace: 'nowrap'
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users_.map(u => {
                  const isOpen = !!expanded[u.user_login];
                  return (
                    <React.Fragment key={u.user_login}>
                      <tr
                        style={{
                          borderBottom: '1px solid var(--border)',
                          cursor: 'pointer'
                        }}
                        onClick={() => toggleExpand(u.user_login)}
                      >
                        <td style={{ padding: '0.4rem 0.6rem', textAlign: 'center', width: 24 }}>
                          <i className={`fas ${isOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`}
                            style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }} />
                        </td>
                        <td style={{ padding: '0.4rem 0.6rem', fontWeight: 600 }}>{u.user_login}</td>
                        <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right' }}>{u.total_shifts ?? 0}</td>
                        <td style={{
                          padding: '0.4rem 0.6rem', textAlign: 'right',
                          color: punctColor(u.punctuality_percent), fontWeight: 700
                        }}>
                          {u.punctuality_percent != null ? `${pl(Number(u.punctuality_percent))}%` : '—'}
                        </td>
                        <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', color: 'var(--success)' }}>
                          {u.on_time_count ?? 0}
                        </td>
                        <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', color: 'var(--danger)' }}>
                          {u.late_count ?? 0}
                        </td>
                        <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', color: 'var(--orange)' }}>
                          {u.absent_count ?? 0}
                        </td>
                        <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right' }}>
                          {u.total_late_minutes ?? 0}
                        </td>
                        <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right' }}>
                          {u.avg_late_minutes != null ? pl(Number(u.avg_late_minutes)) : '0,00'}
                        </td>
                        <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right' }}>
                          {u.total_planned_hours != null ? pl(Number(u.total_planned_hours)) : '0,00'}
                        </td>
                        <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right' }}>
                          {u.total_actual_hours != null ? pl(Number(u.total_actual_hours)) : '0,00'}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr style={{ backgroundColor: 'var(--bg)' }}>
                          <td colSpan={11} style={{ padding: '0.6rem 1rem' }}>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.4rem', fontWeight: 600 }}>
                              Spoznienia i absencje:
                            </div>
                            {(details[u.user_login] || []).filter(s => Number(s.spoznienie_minut || 0) > 0 || s.ocena_punktualnosci === 'nieobecny').length === 0 ? (
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                Brak spoznien i absencji w tym miesiacu.
                              </div>
                            ) : (
                              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                {(details[u.user_login] || [])
                                  .filter(s => Number(s.spoznienie_minut || 0) > 0 || s.ocena_punktualnosci === 'nieobecny')
                                  .map(s => (
                                    <li key={s.id} style={{
                                      padding: '0.25rem 0.5rem',
                                      borderRadius: 12,
                                      fontSize: '0.75rem',
                                      backgroundColor: s.ocena_punktualnosci === 'nieobecny' ? 'var(--orange)' : 'var(--danger)',
                                      color: '#fff',
                                      fontWeight: 500
                                    }}>
                                      {s.data}
                                      {s.ocena_punktualnosci === 'nieobecny'
                                        ? ' — nieobecny'
                                        : ` — ${s.spoznienie_minut} min`}
                                    </li>
                                  ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PunktualnoscRaport;
